import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Badge
} from 'reactstrap';
import { toast } from 'react-toastify';

import {
  fetchPodioAccounts,
  fetchPodioOrgs,
  fetchPodioSpaces,
  fetchPodioApps
} from '../../Redux/Actions/AI/podiolookup';

const BASE_URL = process.env.REACT_APP_PORT;
const authHead = () => ({
  headers: { 'x-auth-token': localStorage.getItem('token') }
});

function PodioSetup({
  accounts,
  orgsByAccount,
  spacesByOrg,
  appsBySpace,
  loading,
  error,
  fetchPodioAccounts,
  fetchPodioOrgs,
  fetchPodioSpaces,
  fetchPodioApps
}) {
  const [setup, setSetup] = useState(null);
  const [accountId, setAccountId] = useState('');
  const [orgId, setOrgId] = useState('');
  const [spaceId, setSpaceId] = useState('');
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Step 4 — Contact app
  const [contactMode, setContactMode] = useState('existing'); // 'existing' | 'installed'
  const [contactAppId, setContactAppId] = useState('');
  const [contactFields, setContactFields] = useState([]);
  const [nameFieldId, setNameFieldId] = useState('');
  const [phoneFieldId, setPhoneFieldId] = useState('');
  const [loadingFields, setLoadingFields] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [finalizingContact, setFinalizingContact] = useState(false);
  // True when the user has selected/changed Contact app fields since the
  // last successful finalize (or fresh install). The "Save Contact app
  // configuration" button only appears when this is true.
  const [contactConfigDirty, setContactConfigDirty] = useState(false);

  // Step 5 — Communication app
  const [commAppId, setCommAppId] = useState('');
  const [installingComm, setInstallingComm] = useState(false);
  const [refreshingComm, setRefreshingComm] = useState(false);

  // Step 6 — Sync toggle
  const [syncEnabled, setSyncEnabled] = useState(false);

  // Initial load: existing setup + accounts, then cascade preloads.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/podio/setup`, authHead());
        const doc = res.data && res.data.data;
        await fetchPodioAccounts();

        if (doc) {
          setSetup(doc);
          if (doc.accountId) {
            setAccountId(doc.accountId);
            await fetchPodioOrgs(doc.accountId);
          }
          if (doc.accountId && doc.organization) {
            setOrgId(doc.organization);
            await fetchPodioSpaces(doc.accountId, doc.organization);
          }
          if (doc.accountId && doc.workspace) {
            setSpaceId(doc.workspace);
            await fetchPodioApps(doc.accountId, doc.workspace);
          }
          if (doc.contactApp) {
            setContactMode(doc.contactApp.source || 'existing');
            setContactAppId(doc.contactApp.appId || '');
            if (doc.contactApp.fieldMap) {
              setNameFieldId(doc.contactApp.fieldMap.name || '');
              setPhoneFieldId(doc.contactApp.fieldMap.phone || '');
            }
            if (doc.contactApp.appId) {
              await loadFields(doc.contactApp.appId);
            }
            // Needs finalize if mapping is complete but the calc field hasn't
            // been created yet (half-finished setup).
            const hasMapping =
              doc.contactApp.fieldMap &&
              doc.contactApp.fieldMap.phone &&
              doc.contactApp.fieldMap.name;
            const hasCalc =
              doc.contactApp.fieldMap && doc.contactApp.fieldMap.cleanedPhone;
            setContactConfigDirty(!!(hasMapping && !hasCalc));
          }
          if (doc.communicationApp && doc.communicationApp.appId) {
            setCommAppId(doc.communicationApp.appId);
          }
          setSyncEnabled(!!doc.syncEnabled);
        }
      } catch (e) {
        toast.error('Failed to load Podio setup');
      } finally {
        setInitializing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = async (patch) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${BASE_URL}/api/podio/setup`,
        patch,
        authHead()
      );
      setSetup(res.data && res.data.data);
    } catch (e) {
      toast.error('Failed to save selection');
    } finally {
      setSaving(false);
    }
  };

  const onAccountChange = async (e) => {
    const v = e.target.value;
    if (
      !confirmIfConfigured(
        'Switching the Podio account will reset your Contact and Communication app configuration. Continue?'
      )
    ) {
      e.target.value = accountId; // revert select UI
      return;
    }
    setAccountId(v);
    setOrgId('');
    setSpaceId('');
    if (v) await fetchPodioOrgs(v);
    await persist({
      accountId: v,
      organization: '',
      workspace: '',
      contactApp: { appId: '', source: 'existing', fieldMap: { name: '', phone: '' } },
      communicationApp: { appId: '', source: 'installed', fields: {} }
    });
  };

  const onOrgChange = async (e) => {
    const v = e.target.value;
    if (
      !confirmIfConfigured(
        'Switching the organization will reset your Contact and Communication app configuration. Continue?'
      )
    ) {
      e.target.value = orgId;
      return;
    }
    setOrgId(v);
    setSpaceId('');
    if (accountId && v) await fetchPodioSpaces(accountId, v);
    await persist({
      organization: v,
      workspace: '',
      contactApp: { appId: '', source: 'existing', fieldMap: { name: '', phone: '' } },
      communicationApp: { appId: '', source: 'installed', fields: {} }
    });
  };

  const onSpaceChange = async (e) => {
    const v = e.target.value;
    if (
      !confirmIfConfigured(
        'Switching the workspace will reset your Contact and Communication app configuration. Continue?'
      )
    ) {
      e.target.value = spaceId;
      return;
    }
    setSpaceId(v);
    setContactMode('existing');
    setContactAppId('');
    setContactFields([]);
    setNameFieldId('');
    setPhoneFieldId('');
    setCommAppId('');
    if (accountId && v) await fetchPodioApps(accountId, v);
    await persist({
      workspace: v,
      contactApp: { appId: '', source: 'existing', fieldMap: { name: '', phone: '' } },
      communicationApp: { appId: '', source: 'installed', fields: {} }
    });
  };

  const loadFields = async (appId) => {
    if (!appId) {
      setContactFields([]);
      return;
    }
    setLoadingFields(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/podio/apps/${appId}/fields`,
        authHead()
      );
      const fields = (res.data && res.data.data && res.data.data.fields) || [];
      setContactFields(fields);
    } catch (e) {
      toast.error('Failed to load app fields');
      setContactFields([]);
    } finally {
      setLoadingFields(false);
    }
  };

  const onContactModeChange = async (mode) => {
    if (mode === contactMode) return;
    if (setup && setup.contactApp && setup.contactApp.appId) {
      // eslint-disable-next-line no-alert
      if (
        !window.confirm(
          'Changing the Contact app source will reset the current Contact app configuration. The Communication app stays installed. Continue?'
        )
      ) {
        return;
      }
    }
    setContactMode(mode);
    setContactAppId('');
    setContactFields([]);
    setNameFieldId('');
    setPhoneFieldId('');
    setContactConfigDirty(false);
    await persist({
      contactApp: { appId: '', source: mode, fieldMap: { name: '', phone: '' } }
    });
  };

  const onExistingAppChange = async (e) => {
    const v = e.target.value;
    if (setup && setup.contactApp && setup.contactApp.appId && setup.contactApp.appId !== v) {
      // eslint-disable-next-line no-alert
      if (
        !window.confirm(
          'Replacing the Contact app will reset the Name and Phone field mapping. After remapping, click "Save Contact app configuration" to apply changes. Continue?'
        )
      ) {
        e.target.value = contactAppId;
        return;
      }
    }
    setContactAppId(v);
    setNameFieldId('');
    setPhoneFieldId('');
    setContactConfigDirty(false); // mapping was wiped; will go true once user re-maps
    await persist({
      contactApp: { appId: v, source: 'existing', fieldMap: { name: '', phone: '' } }
    });
    await loadFields(v);
  };

  // Name/Phone selections are kept in local state only — nothing is written
  // to the backend until the user clicks "Save Contact app configuration".
  // This guards against accidental dropdown clicks wiping the existing
  // calc-field mapping (cleanedPhone) on the saved setup doc.
  const onNameFieldChange = (e) => {
    setNameFieldId(e.target.value);
    setContactConfigDirty(true);
  };

  const onPhoneFieldChange = (e) => {
    setPhoneFieldId(e.target.value);
    setContactConfigDirty(true);
  };

  const onSyncToggle = async (e) => {
    const v = e.target.checked;
    setSyncEnabled(v);
    await persist({ syncEnabled: v });
  };

  // Confirms a destructive change against the current state of the wizard.
  // Returns true if it's safe to proceed (no current commitment, or user said OK).
  const confirmIfConfigured = (msg) => {
    const hasContactApp = !!(setup && setup.contactApp && setup.contactApp.appId);
    const hasCommApp = !!commAppId;
    if (!hasContactApp && !hasCommApp) return true;
    // eslint-disable-next-line no-alert
    return window.confirm(msg);
  };

  const finalizeContactConfig = async () => {
    setFinalizingContact(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/api/podio/setup/finalize-contact-app`,
        { fieldMap: { name: nameFieldId, phone: phoneFieldId } },
        authHead()
      );
      const data = res.data && res.data.data;
      if (data) {
        setSetup(data);
        setContactConfigDirty(false);
        toast.success('Contact app configuration saved');
      }
    } catch (e) {
      const msg =
        (e.response && e.response.data && e.response.data.error) ||
        'Failed to save Contact app configuration';
      toast.error(msg);
    } finally {
      setFinalizingContact(false);
    }
  };

  const refreshCommunicationApp = async () => {
    setRefreshingComm(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/api/podio/refresh/communication-app`,
        {},
        authHead()
      );
      const data = res.data && res.data.data;
      if (data) {
        setSetup(data);
        toast.success('Communication app mappings refreshed');
      }
    } catch (e) {
      const msg =
        (e.response && e.response.data && e.response.data.error) ||
        'Failed to refresh mappings';
      toast.error(msg);
    } finally {
      setRefreshingComm(false);
    }
  };

  const installCommunicationApp = async () => {
    setInstallingComm(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/api/podio/install/communication-app`,
        {},
        authHead()
      );
      const data = res.data && res.data.data;
      if (data) {
        setCommAppId(data.appId);
        if (data.setup) setSetup(data.setup);
        toast.success('Communication app installed');
      }
    } catch (e) {
      const msg =
        (e.response && e.response.data && e.response.data.error) ||
        'Failed to install Communication app';
      toast.error(msg);
    } finally {
      setInstallingComm(false);
    }
  };

  const installContactApp = async () => {
    if (setup && setup.contactApp && setup.contactApp.appId) {
      // eslint-disable-next-line no-alert
      if (
        !window.confirm(
          'A Contact app is already configured. Installing a fresh Mergecall Contacts app will replace it (the Communication app stays installed and will be re-pointed to the new Contact app). Continue?'
        )
      ) {
        return;
      }
    }
    setInstalling(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/api/podio/install/contact-app`,
        {},
        authHead()
      );
      const data = res.data && res.data.data;
      if (data) {
        setContactAppId(data.appId);
        setContactFields(data.fields || []);
        if (data.setup) {
          setSetup(data.setup);
          if (data.setup.contactApp && data.setup.contactApp.fieldMap) {
            setNameFieldId(data.setup.contactApp.fieldMap.name || '');
            setPhoneFieldId(data.setup.contactApp.fieldMap.phone || '');
          }
        }
        setContactConfigDirty(false);
        toast.success('Contact app installed');
      }
    } catch (e) {
      const msg =
        (e.response && e.response.data && e.response.data.error) ||
        'Failed to install Contact app';
      toast.error(msg);
    } finally {
      setInstalling(false);
    }
  };

  const orgs = (accountId && orgsByAccount[accountId]) || [];
  const spaces = (orgId && spacesByOrg[orgId]) || [];
  const apps = (spaceId && appsBySpace[spaceId]) || [];
  const nameFieldOptions = contactFields.filter(
    (f) => f.type === 'text' && f.size === 'small'
  );
  const phoneFieldOptions = contactFields.filter((f) => f.type === 'phone');

  const contactStepComplete =
    contactMode === 'installed'
      ? !!contactAppId
      : !!(contactAppId && nameFieldId && phoneFieldId);

  const statusBadge = () => {
    const s = setup && setup.status;
    if (s === 'configured') return <Badge color="success">Configured</Badge>;
    if (s === 'error') return <Badge color="danger">Error</Badge>;
    return <Badge color="secondary">Pending</Badge>;
  };

  if (initializing) {
    return (
      <Row className="justify-content-center mt-5">
        <Spinner color="primary" />
      </Row>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col md="9">
        <Card>
          <CardHeader>
            <CardTitle tag="h4" className="d-flex justify-content-between align-items-center mb-0">
              <span>Podio Setup</span>
              {statusBadge()}
            </CardTitle>
            <p className="text-muted mt-2 mb-0">
              Connect a Podio account, pick the org and workspace, then choose your Contact app
              and install the Communication app. Once configured, new calls and SMS will be
              logged into Podio automatically.
            </p>
          </CardHeader>
          <CardBody>
            {error && <Alert color="danger">{error}</Alert>}

            {/* Step 1 — Podio account */}
            <Card className="mb-3">
              <CardBody>
                <h5>1. Podio account</h5>
                <p className="text-muted small mb-2">
                  Accounts connected on your Mergeasy profile. Add a new one from mergeasy.com if
                  it's not listed.
                </p>
                <FormGroup>
                  <Label for="podio-account">Account</Label>
                  <Input
                    id="podio-account"
                    type="select"
                    value={accountId}
                    onChange={onAccountChange}
                    disabled={loading || saving}
                  >
                    <option value="">-- Select Podio account --</option>
                    {accounts.map((a) => (
                      <option key={a._id || a.id || a.accountId} value={a._id || a.id || a.accountId}>
                        {a.name || a.email || a._id || a.id}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </CardBody>
            </Card>

            {/* Step 2 — Organization */}
            {accountId && (
              <Card className="mb-3">
                <CardBody>
                  <h5>2. Organization</h5>
                  <FormGroup>
                    <Label for="podio-org">Organization</Label>
                    <Input
                      id="podio-org"
                      type="select"
                      value={orgId}
                      onChange={onOrgChange}
                      disabled={loading || saving}
                    >
                      <option value="">-- Select organization --</option>
                      {orgs.map((o) => (
                        <option key={o.org_id} value={o.org_id}>
                          {o.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </CardBody>
              </Card>
            )}

            {/* Step 3 — Workspace */}
            {accountId && orgId && (
              <Card className="mb-3">
                <CardBody>
                  <h5>3. Workspace</h5>
                  <FormGroup>
                    <Label for="podio-space">Workspace</Label>
                    <Input
                      id="podio-space"
                      type="select"
                      value={spaceId}
                      onChange={onSpaceChange}
                      disabled={loading || saving}
                    >
                      <option value="">-- Select workspace --</option>
                      {spaces.map((s) => (
                        <option key={s.space_id} value={s.space_id}>
                          {s.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </CardBody>
              </Card>
            )}

            {/* Step 4 — Contact app */}
            {spaceId && (
              <Card className="mb-3">
                <CardBody>
                  <h5>4. Contact app</h5>
                  <p className="text-muted small mb-2">
                    Pick the Contact app new call/SMS contacts should be stored in. Use an
                    existing app and map its Name &amp; Phone fields, or install a fresh
                    Mergecall Contacts app into this workspace.
                  </p>

                  <FormGroup tag="fieldset" className="mb-3">
                    <FormGroup check inline>
                      <Label check>
                        <Input
                          type="radio"
                          name="contactMode"
                          checked={contactMode === 'existing'}
                          onChange={() => onContactModeChange('existing')}
                          disabled={installing || saving}
                        />{' '}
                        Use existing app
                      </Label>
                    </FormGroup>
                    <FormGroup check inline>
                      <Label check>
                        <Input
                          type="radio"
                          name="contactMode"
                          checked={contactMode === 'installed'}
                          onChange={() => onContactModeChange('installed')}
                          disabled={installing || saving}
                        />{' '}
                        Install new app
                      </Label>
                    </FormGroup>
                  </FormGroup>

                  {contactMode === 'existing' && (
                    <>
                      <FormGroup>
                        <Label for="contact-app">Contact app</Label>
                        <Input
                          id="contact-app"
                          type="select"
                          value={contactAppId}
                          onChange={onExistingAppChange}
                          disabled={loading || saving}
                        >
                          <option value="">-- Select Contact app --</option>
                          {apps.map((a) => (
                            <option key={a.app_id} value={a.app_id}>
                              {(a.config && a.config.name) || a.name || `App ${a.app_id}`}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>

                      {contactAppId && (
                        <Row>
                          <Col md="6">
                            <FormGroup>
                              <Label for="name-field">Name field</Label>
                              <Input
                                id="name-field"
                                type="select"
                                value={nameFieldId}
                                onChange={onNameFieldChange}
                                disabled={loadingFields || saving}
                              >
                                <option value="">-- Select field --</option>
                                {nameFieldOptions.map((f) => (
                                  <option key={`n-${f.id}`} value={f.id}>
                                    {f.label}
                                  </option>
                                ))}
                              </Input>
                              {!loadingFields && nameFieldOptions.length === 0 && (
                                <small className="text-muted">
                                  No single-line text fields found in this app.
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md="6">
                            <FormGroup>
                              <Label for="phone-field">Phone field</Label>
                              <Input
                                id="phone-field"
                                type="select"
                                value={phoneFieldId}
                                onChange={onPhoneFieldChange}
                                disabled={loadingFields || saving}
                              >
                                <option value="">-- Select field --</option>
                                {phoneFieldOptions.map((f) => (
                                  <option key={`p-${f.id}`} value={f.id}>
                                    {f.label}
                                  </option>
                                ))}
                              </Input>
                              {!loadingFields && phoneFieldOptions.length === 0 && (
                                <small className="text-muted">
                                  No phone fields found in this app.
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                      )}

                      {loadingFields && (
                        <div className="text-muted small">
                          <Spinner size="sm" /> Loading fields…
                        </div>
                      )}

                      {contactAppId &&
                        nameFieldId &&
                        phoneFieldId &&
                        contactConfigDirty && (
                          <div className="mt-2">
                            <Button
                              color="primary"
                              onClick={finalizeContactConfig}
                              disabled={finalizingContact}
                            >
                              {finalizingContact ? (
                                <>
                                  <Spinner size="sm" /> Saving…
                                </>
                              ) : (
                                'Save Contact app configuration'
                              )}
                            </Button>
                            <small className="text-muted d-block mt-2">
                              Adds a hidden phone-cleanup helper to this app
                              and, if the Communication app is already
                              installed, re-points its Contact reference to
                              this app.
                            </small>
                          </div>
                        )}
                    </>
                  )}

                  {contactMode === 'installed' && (
                    <>
                      {contactAppId ? (
                        <Alert color="success" className="mb-0">
                          Installed Mergecall Contacts app (ID: {contactAppId}). Name and Phone
                          fields are auto-mapped.
                        </Alert>
                      ) : (
                        <Button
                          color="primary"
                          onClick={installContactApp}
                          disabled={installing}
                        >
                          {installing ? (
                            <>
                              <Spinner size="sm" /> Installing…
                            </>
                          ) : (
                            'Install Contact app'
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Step 5 — Communication app */}
            {spaceId && (
              <Card className="mb-3">
                <CardBody>
                  <h5>5. Communication app</h5>
                  <p className="text-muted small mb-2">
                    Installs the Mergecall Communications app into this workspace. New calls and
                    SMS will be logged here as items, each linked to the matching Contact.
                    A hidden phone-cleanup helper field will also be added to your Contact app
                    so number lookups work regardless of formatting (e.g. "(800) 555-1234"
                    vs "+18005551234"). Re-installing later refreshes that helper in place.
                  </p>

                  {!contactStepComplete && (
                    <Alert color="info" className="mb-0">
                      Finish the Contact app step above first — the Communication app references it.
                    </Alert>
                  )}

                  {contactStepComplete && commAppId && (
                    <>
                      <Alert color="success" className="mb-2">
                        Installed Mergecall Communications app (ID: {commAppId}).
                      </Alert>
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={refreshCommunicationApp}
                        disabled={refreshingComm}
                      >
                        {refreshingComm ? (
                          <>
                            <Spinner size="sm" /> Refreshing…
                          </>
                        ) : (
                          'Refresh field mappings'
                        )}
                      </Button>
                      <small className="text-muted d-block mt-2">
                        Re-reads the app's fields from Podio. Use this if some
                        fields aren't populating on synced items.
                      </small>
                    </>
                  )}

                  {contactStepComplete && !commAppId && (
                    <Button
                      color="primary"
                      onClick={installCommunicationApp}
                      disabled={installingComm}
                    >
                      {installingComm ? (
                        <>
                          <Spinner size="sm" /> Installing…
                        </>
                      ) : (
                        'Install Communication app'
                      )}
                    </Button>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Step 6 — Sync toggle */}
            {commAppId && contactStepComplete && (
              <Card className="mb-3">
                <CardBody>
                  <h5>6. Sync</h5>
                  <p className="text-muted small mb-2">
                    When enabled, every new call and SMS will be logged to the Communication
                    app and linked to the matching Contact (creating a Contact if it's a new
                    number).
                  </p>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={syncEnabled}
                        onChange={onSyncToggle}
                        disabled={saving}
                      />{' '}
                      Sync new calls and SMS to Podio
                    </Label>
                  </FormGroup>
                  {setup && setup.lastError && (
                    <Alert color="warning" className="mt-3 mb-0">
                      Last sync error: {setup.lastError}
                    </Alert>
                  )}
                </CardBody>
              </Card>
            )}

            {saving && (
              <div className="text-muted small">
                <Spinner size="sm" /> Saving…
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}

const mapStateToProps = (state) => ({
  accounts: state.podioLookup.accounts,
  orgsByAccount: state.podioLookup.orgsByAccount,
  spacesByOrg: state.podioLookup.spacesByOrg,
  appsBySpace: state.podioLookup.appsBySpace,
  loading: state.podioLookup.loading,
  error: state.podioLookup.error
});

export default connect(mapStateToProps, {
  fetchPodioAccounts,
  fetchPodioOrgs,
  fetchPodioSpaces,
  fetchPodioApps
})(PodioSetup);
